import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { IMeter, IPage } from 'common/src/types/interfaces';

import { QUERY_KEYS } from 'portal/src/api/constants';
import { getMeters, updateMeter } from 'portal/src/api/MetersService';
import useAuth from 'portal/src/auth/useAuth';
import type { DeactivateConfig } from 'portal/src/components/common/CustomToolbar';
import DataTable from 'portal/src/components/common/DataTable';
import { NEW_ID } from 'portal/src/constants/ReservedIDs';

import buildMetersColumns from './buildMetersColumns';

import ViewAccountBills from '../Form/Bills/ViewAccountBills';

const Meters = () => {
  const navigate = useNavigate();
  const { featureChecker } = useAuth();

  const [showDeactivatedMeters, setShowDeactivatedMeters] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    department: false,
  });
  const [viewBills, setViewBills] = useState<{
    open: boolean;
    data: Partial<IMeter>;
  }>({ open: false, data: {} });

  const {
    data: meters,
    isError,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEYS.meters],
    queryFn: async () => await getMeters(),
    initialData: { data: [] } as IPage<IMeter>,
  });

  const handleNavigate = (id: string) => navigate(`./${id}`);

  const deactivateConfig: DeactivateConfig = {
    isChecked: showDeactivatedMeters,
    onToggleCheckbox: setShowDeactivatedMeters,
  };

  return viewBills.open ? (
    <ViewAccountBills
      accountId={viewBills.data.parentAccount.id}
      handleBackAction={() => setViewBills({ open: false, data: {} })}
    />
  ) : (
    <DataTable
      actionsConfig={{
        readActionConfig: {
          onClick: (id) => handleNavigate(id),
        },
        changeActiveConfig: {
          targetStr: 'meter',
          onChangeActiveStatus: async (id, isActive) => {
            await updateMeter(id, {
              isActive: !isActive,
            });
            await refetch();
          },
          snackbarMsgKey: 'METER',
        },
        extraActions: ({ id }) => [
          {
            icon: <DatasetOutlinedIcon />,
            label: 'View Bills',
            onClick: () => {
              const meter = meters.data.find(
                ({ id: meterId }) => meterId === id,
              );
              if (meter) {
                setViewBills({ open: true, data: meter });
              }
            },
          },
        ],
      }}
      columns={buildMetersColumns(featureChecker)}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
      contextName={NESTED_ROUTES.ADMINISTRATION.METERS}
      dataFetchError={isError}
      loading={!isFetched}
      rows={
        showDeactivatedMeters
          ? meters.data
          : meters.data.filter(({ isActive }) => isActive)
      }
      toolbarSlotProps={{
        deactivateConfig,
        exportFileName: 'meters',
        onNewButtonClick: () => handleNavigate(NEW_ID),
        showColumns: true,
        showFilters: true,
      }}
    />
  );
};

export default Meters;
